import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { object, string } from '@calendly/form/validation'
import { Button } from '@calendly/ui/components/button'
import { TextField } from '@calendly/form'
import { httpRequest } from '@calendly/http-tools'

import { useI18n } from 'locales'
import { formatAmount } from 'utils/currency'
import { computeInvoiceTotals } from 'utils/invoice-helpers'

import {
  FormWrapper,
  StyledForm,
  FormFieldsWrapper,
  TotalDueSection,
  TotalDueLabel,
  TotalDueAmount,
  PaymentSection,
  PaymentSectionTitle,
  BottomSection,
  SubmitErrorMessage,
} from './styles'
import { TermsAndPrivacyNotice } from '../package/terms_and_privacy_notice'
import InvoiceStripeContainer from './InvoiceStripeContainer'
import { InvoiceStripeForm } from './InvoiceStripeForm'
import { useInvoicePageData } from './InvoiceContext'

const InvoicePaymentForm = () => {
  const { i18n } = useI18n()
  const { invoiceUuid } = useParams()
  const { invoice, setInvoice } = useInvoicePageData()
  const [paymentApi, setPaymentApi] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [isStale, setIsStale] = useState(false)

  const currency = invoice?.invoice_settings?.currency || 'USD'
  const { totalCents } = computeInvoiceTotals(invoice)
  const invoicePrice = formatAmount(totalCents / 100, currency)

  const validationSchema = object({
    name: string()
      .trim()
      .required(i18n.t('booking.package.validation.name_required'))
      .max(155, i18n.t('booking.package.validation.name_max_length')),
    email: string()
      .trim()
      .required(i18n.t('booking.package.validation.email_required'))
      .email(i18n.t('booking.package.validation.email_invalid')),
  })

  const handleSubmit = async (values) => {
    if (isSubmitting || isStale || !paymentApi?.createPayment) return

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Re-fetch the invoice immediately before charging Stripe so that a
      // payment completed in another tab cannot race past the capture-time check.
      const [freshOk, freshRes] = await httpRequest({
        url: `/api/booking/invoices/${invoiceUuid}`,
        method: 'GET',
      })

      if (!freshOk) {
        setSubmitError(i18n.t('booking.invoice.errors.freshness_check_failed'))
        setIsSubmitting(false)
        return
      }

      if (freshRes?.resource?.status?.toUpperCase() === 'PAID') {
        setIsStale(true)
        setSubmitError(i18n.t('booking.invoice.errors.stale_invoice_status'))
        setIsSubmitting(false)
        return
      }

      const paymentSuccess = await paymentApi.createPayment()
      if (!paymentSuccess) {
        setIsSubmitting(false)
        return
      }

      const paymentData = paymentApi.getPaymentData()

      const [ok, res] = await httpRequest({
        url: `/api/booking/invoices/${invoiceUuid}/stripe_payment_intents/capture`,
        method: 'POST',
        data: {
          payment_intent_client_secret:
            paymentData.payment_intent_client_secret,
          receipt_email: values.email,
          purchaser_name: values.name,
        },
      })

      if (!ok) {
        setSubmitError(
          i18n.t('booking.package.errors.payment_complete_error'),
        )
        setIsSubmitting(false)
        return
      }

      if (res?.resource?.invoice) {
        setInvoice(res.resource.invoice)
      }
    } catch (_error) {
      setSubmitError(i18n.t('booking.package.errors.payment_complete_error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const billedToName = invoice?.contact?.full_name?.trim() || ''
  const billedToEmail = invoice?.contact?.primary_email?.trim() || ''

  return (
    <FormWrapper>
      <StyledForm
        initialValues={{
          name: billedToName,
          email: billedToEmail,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <FormFieldsWrapper>
          <TextField
            id="name"
            name="name"
            label={i18n.t('booking.package.name_label')}
            required
            maxLength={155}
            placeholder={i18n.t('booking.package.name_placeholder')}
            autoComplete="name"
          />

          <TextField
            id="email"
            name="email"
            type="email"
            label={i18n.t('booking.package.email_label')}
            placeholder={i18n.t('booking.package.email_placeholder')}
            required
            autoComplete="email"
          />

          <TotalDueSection>
            <TotalDueLabel>
              {i18n.t('booking.package.total_due')}
            </TotalDueLabel>
            <TotalDueAmount>{invoicePrice}</TotalDueAmount>
          </TotalDueSection>

          <PaymentSection>
            <PaymentSectionTitle>
              {i18n.t('booking.package.payment_title')}
            </PaymentSectionTitle>
            <InvoiceStripeContainer>
              {(clientSecret) => (
                <InvoiceStripeForm
                  registerApi={setPaymentApi}
                  clientSecret={clientSecret}
                />
              )}
            </InvoiceStripeContainer>
          </PaymentSection>
        </FormFieldsWrapper>

        <BottomSection>
          {submitError && <SubmitErrorMessage>{submitError}</SubmitErrorMessage>}

          <Button
            type="submit"
            wide
            disabled={isSubmitting || isStale || !paymentApi}
            loading={isSubmitting}
          >
            {isSubmitting
              ? i18n.t('booking.package.processing_button')
              : i18n.t('booking.package.purchase_button')}
          </Button>
          <TermsAndPrivacyNotice />
        </BottomSection>
      </StyledForm>
    </FormWrapper>
  )
}

export default InvoicePaymentForm
